import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Snackbar } from '@mui/material'
import { SWRConfig } from 'swr'
import { SnackbarProvider } from 'notistack'
import Loader from 'components/Loader'
import { fetchUser, fetchUserFavorites } from 'modules/User/actions'
import { FETCH_USER } from 'modules/User/actionTypes'
import {
  checkSession, getSession, handleSessionStorage, isCurrentSession,
  SESSION_CHANGED, SESSION_EXPIRED_MESSAGE, shouldValidateSession, subscribeSession,
} from 'utils/session'
import { currentPageUrl, loginUrl } from 'utils/loginRedirect'

const cacheOptions = {
  provider: () => new Map(),
  shouldRetryOnError: error => !error.sessionHandled && error.status !== 401,
}

const SessionBoundary = ({ children }) => {
  const session = useSyncExternalStore(subscribeSession, getSession)
  const user = useSelector(state => state.user)
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const pending = useRef(null)
  const handledLogout = useRef(-1)
  const [dismissedNotice, setDismissedNotice] = useState(-1)
  const protectedPage = /^\/(account|business|business-account)(\/|$)/.test(location.pathname)

  useLayoutEffect(() => {
    dispatch({ type: SESSION_CHANGED, generation: session.generation, hasToken: Boolean(session.token) })
  }, [dispatch, session.generation, session.token])

  const validate = useCallback(() => {
    const context = checkSession()
    if (!context.token || context.ending) return
    if (pending.current?.generation === context.generation) return pending.current.promise
    const promise = dispatch(fetchUser()).then(result => {
      if (result && isCurrentSession(context)) {
        // Favorites have their own error UI; their failure does not invalidate authentication.
        dispatch(fetchUserFavorites()).catch(() => {})
      }
    }).finally(() => {
      if (pending.current?.promise === promise) pending.current = null
    })
    pending.current = { generation: context.generation, promise }
    return promise
  }, [dispatch])

  useEffect(() => {
    validate()
  }, [validate, session.generation])

  useLayoutEffect(() => {
    checkSession()
  }, [location.pathname, location.search, location.hash])

  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === 'hidden') return
      const current = checkSession()
      if (current.token && !current.ending && shouldValidateSession()) validate()
    }
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') checkSession()
    }, 30000)
    window.addEventListener('focus', resume)
    window.addEventListener('online', resume)
    window.addEventListener('storage', handleSessionStorage)
    document.addEventListener('visibilitychange', resume)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', resume)
      window.removeEventListener('online', resume)
      window.removeEventListener('storage', handleSessionStorage)
      document.removeEventListener('visibilitychange', resume)
    }
  }, [validate])

  useLayoutEffect(() => {
    if (session.reason === 'logout' && location.pathname === '/' && location.search === '?logout=1') {
      handledLogout.current = session.generation
    }
  }, [session.generation, session.reason, location.pathname, location.search])

  if (session.ending || user.sessionGeneration !== session.generation) return <Loader />
  if (session.reason === 'logout' && handledLogout.current !== session.generation
    && !(location.pathname === '/' && location.search === '?logout=1')) {
    return <Navigate to="/?logout=1" replace />
  }
  if (protectedPage && !session.token) {
    return <Navigate to={loginUrl(currentPageUrl(location), session.reason === 'expired')} replace />
  }

  const retry = () => {
    if (!user.details) dispatch({ type: FETCH_USER })
    validate()
  }

  return (
    <>
      {user.sessionError && (
        <Box sx={{ position: 'relative', zIndex: 1400 }}>
          <Alert severity="warning" action={<Button color="inherit" onClick={retry}>Try again</Button>}>
            We couldn’t verify your session. Check your connection and try again.
          </Alert>
        </Box>
      )}
      {!(protectedPage && user.sessionError && !user.details) && (
        <SWRConfig key={session.generation} value={cacheOptions}>
          <SnackbarProvider
            maxSnack={3}
            autoHideDuration={4000}
            disableWindowBlurListener
            anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          >
            {children}
          </SnackbarProvider>
        </SWRConfig>
      )}
      <Snackbar
        open={session.reason === 'expired' && location.pathname !== '/login' && dismissedNotice !== session.generation}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" action={
          <>
            <Button color="inherit" onClick={() => navigate(loginUrl(currentPageUrl(location), true))}>Sign in</Button>
            <Button color="inherit" onClick={() => setDismissedNotice(session.generation)}>Dismiss</Button>
          </>
        }>
          {SESSION_EXPIRED_MESSAGE}
        </Alert>
      </Snackbar>
    </>
  )
}

export default SessionBoundary
